import { useState } from 'react'
import { ArrowRight, ShieldCheck, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DEMO_SAFE } from '../lib/constants.js'
import { parseSafeInput } from '../lib/resolveSafe.js'

/**
 * Public landing screen shown when a visitor opens the bare URL. We don't
 * advertise any specific Safe pair here — the visitor either supplies their
 * own (we resolve it via `?safe=`) or follows the demo link.
 */
export function LandingCard() {
  const [input, setInput] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const submit = () => {
    const addr = parseSafeInput(input)
    if (!addr) {
      setErr(
        'Paste a Safe address (0x… 40-hex) or a Safe{Wallet} URL.',
      )
      return
    }
    const params = new URLSearchParams(window.location.search)
    params.set('safe', addr)
    window.location.search = params.toString()
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <ShieldCheck className="size-5 text-primary" />
          Welcome to SafeWard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          SafeWard adds a Guardian co-signer to your Safe. Every dApp
          transaction routes through a "Module Safe" that you and the Guardian
          both have to approve before it executes — the Guardian runs a
          smart-permit policy in the background and rejects anything that
          touches a non-whitelisted spender or moves USD out of your Safe.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
          className="space-y-2"
        >
          <Label htmlFor="safe-input">Open your Safe</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="safe-input"
              autoFocus
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                setErr(null)
              }}
              placeholder="0x… or paste a Safe{Wallet} URL"
              className="font-mono"
            />
            <Button type="submit">
              <ArrowRight />
              Open
            </Button>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <p className="text-xs text-muted-foreground">
            We accept either the Module Safe (the queue we co-sign) or the Main
            Safe it's enabled on — same destination either way.
          </p>
        </form>
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost">
          <a href={`?safe=${DEMO_SAFE}`}>
            <Wand2 />
            Try the demo Safe
          </a>
        </Button>
        <a
          href="https://github.com/arein/safeward"
          target="_blank"
          rel="noopener"
          className="ml-auto text-xs text-muted-foreground hover:text-foreground"
        >
          source on GitHub →
        </a>
      </CardFooter>
    </Card>
  )
}
